import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Panel, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Timeline } from "@/pages/ops/Advisories";

const STATUSES = ["Submitted", "Acknowledged", "Crew Dispatched", "Resolved", "Closed"];

function ReportDetail({ r, onChanged }) {
  const [s, setS] = useState({ status: r.status, message: "" });
  const save = async () => {
    try { await api.post(`/admin/reports/${r.reference}/status`, s); toast.success("Status updated; consumer notified"); setS({ ...s, message: "" }); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel testId="report-detail">
      <p className="font-semibold">{r.category} <span className="font-mono text-xs text-slate-500">{r.reference}</span></p>
      <p className="text-xs text-slate-500 break-words">Account {r.account_number} · {[r.location_text, r.barangay, r.municipality].filter(Boolean).join(", ")}
        {r.latitude != null ? ` · ${r.latitude}, ${r.longitude}` : ""} · observed {fmtDate(r.observed_at)}</p>
      {r.contact_method && <p className="text-xs text-slate-500">Contact via {r.contact_method}: {r.contact_detail}</p>}
      <p className="text-sm mt-3 whitespace-pre-wrap break-words">{r.description}</p>
      <div className="grid lg:grid-cols-2 gap-6 mt-5">
        <Timeline items={r.timeline} testId="report-timeline" />
        <div className="space-y-2"><p className="text-sm font-medium">Update status</p>
          <NativeSelect value={s.status} onChange={(v) => setS({ ...s, status: v })} options={STATUSES.map((x) => ({ value: x, label: x }))} testId="report-status-select" />
          <Textarea rows={2} placeholder="Message to the consumer" value={s.message} onChange={(e) => setS({ ...s, message: e.target.value })} data-testid="report-status-message" />
          <Button size="sm" disabled={s.message.trim().length < 3} onClick={save} data-testid="report-status-submit">Save and notify</Button></div>
      </div>
    </Panel>
  );
}

export default function Reports() {
  const [f, setF] = useState({ status: "", q: "", page: 1 });
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/reports", { params: f }).then((r) => { setData(r.data); if (sel) setSel(r.data.items.find((x) => x.id === sel.id) || sel); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [JSON.stringify(f)]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageHeader eyebrow="Operations" title="Power interruption reports" description="Reports sent by consumers from the mobile app." />
      {data && <div className="flex flex-wrap gap-2 mb-4" data-testid="report-counts">{STATUSES.map((s) => (
        <button key={s} onClick={() => setF({ ...f, status: f.status === s ? "" : s, page: 1 })} data-testid={`report-count-${s.replace(/ /g, "-")}`}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.status === s ? "bg-blue-700 text-white border-blue-700" : "bg-white hover:bg-slate-50"}`}>{s} · {data.counts[s]}</button>))}</div>}
      <Input className="mb-4 max-w-md" placeholder="Search reference, account, area, description" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} data-testid="report-search" />
      <div className="grid lg:grid-cols-[minmax(280px,380px)_1fr] gap-4 min-w-0">
        <div className="bg-white border border-slate-200 rounded-lg min-w-0">
          {!data ? <p className="p-4 text-sm text-slate-500">Loading…</p> : data.items.length === 0 ? <div className="p-4"><Empty>No reports.</Empty></div> : data.items.map((r) => (
            <button key={r.id} onClick={() => setSel(r)} data-testid={`report-row-${r.reference}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${sel?.id === r.id ? "bg-blue-50" : ""}`}>
              <div className="flex justify-between gap-2"><span className="text-sm font-medium truncate">{r.category}</span><StatusBadge status={r.status} /></div>
              <p className="text-xs text-slate-500 truncate">{r.reference} · {r.account_number} · {r.barangay || r.location_text}</p>
              <p className="text-[11px] text-slate-400">{fmtDate(r.created_at)}</p></button>))}
          {data && <div className="flex justify-between items-center p-3 text-xs"><span>{data.total} report{data.total === 1 ? "" : "s"}</span>
            <div className="flex gap-2"><Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="report-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={f.page * 25 >= data.total} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="report-next">Next</Button></div></div>}
        </div>
        <div className="min-w-0">{sel ? <ReportDetail key={sel.id + sel.updated_at} r={sel} onChanged={load} /> : <Panel><Empty>Select a report.</Empty></Panel>}</div>
      </div>
    </div>
  );
}
